import ApiInstance from "./ApiInstance";

export interface AuthUser {
  id: string;
  email: string;
  role: "client" | "psychologist" | "org_admin" | "platform_admin";
  name?: string;
  mfaEnabled?: boolean;
  mfaRequired?: boolean; // role must use MFA but hasn't enabled it yet
}

export interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
}

export type LoginResult = AuthUser | MfaChallenge;
export const isMfaChallenge = (r: LoginResult): r is MfaChallenge => (r as MfaChallenge).mfaRequired === true && !!(r as MfaChallenge).mfaToken;

class AuthApi {
  async register(body: { email: string; password: string; name?: string; role?: string }): Promise<AuthUser> {
    const { data } = await ApiInstance.getApi().post("/auth/register", body);
    return data;
  }
  async login(body: { email: string; password: string }): Promise<LoginResult> {
    const { data } = await ApiInstance.getApi().post("/auth/login", body);
    return data;
  }
  async logout(): Promise<void> {
    await ApiInstance.getApi().post("/auth/logout", {});
  }
  async me(): Promise<AuthUser | null> {
    try {
      const { data } = await ApiInstance.getApi().get("/auth/me");
      return { id: data._id ?? data.id, email: data.email, role: data.role, name: data.name, mfaEnabled: data.mfaEnabled, mfaRequired: data.mfaRequired };
    } catch {
      return null;
    }
  }
  // --- Password reset ---
  async passwordResetRequest(email: string): Promise<void> {
    await ApiInstance.getApi().post("/auth/password-reset-request", { email });
  }
  async passwordResetConfirm(token: string, newPassword: string): Promise<void> {
    await ApiInstance.getApi().post("/auth/password-reset-confirm", { token, newPassword });
  }
  // --- MFA ---
  async mfaSetup(): Promise<{ secret: string; otpauthUri: string }> {
    const { data } = await ApiInstance.getApi().post("/auth/mfa/setup", {});
    return data;
  }
  async mfaEnable(code: string): Promise<{ recoveryCodes: string[] }> {
    const { data } = await ApiInstance.getApi().post("/auth/mfa/enable", { code });
    return data;
  }
  async mfaVerify(mfaToken: string, code: string): Promise<AuthUser> {
    const { data } = await ApiInstance.getApi().post("/auth/mfa/verify", { mfaToken, code });
    return data;
  }
  async mfaDisable(code: string): Promise<void> {
    await ApiInstance.getApi().post("/auth/mfa/disable", { code });
  }
}

const authApi = new AuthApi();
export default authApi;
